import { eqDeepStrict } from '@ipfabric/utils/fp/comparison';
import { memo, useEffect, useState } from 'react';
import type { FC } from 'react';

import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { useManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import {
  type GraphInitialParameters,
  type GraphLinkArgs,
  isGraphSidePanelContentId,
} from '@ipfabric/utils';
import { Graph } from 'components/Graph/Graph';
import { isPathLookupDataInputTabId } from 'components/Graph/GraphSidePanelData';
import { showSidePanel } from 'components/Graph/commands/showSidePanel';
import { useGraph } from 'components/Graph/useGraph';
import { useNavigate } from 'react-router-dom';
import { replaceHistoryStateField } from 'utils/history/stateFields';
import { useIpfLocation } from 'utils/history/useIpfLocation';
import { useKeepIdentityIfEqual } from 'utils/hooks/useKeepIdentityIfEqual';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';

const useGraphLinkArgs = ():
  | undefined
  | GraphLinkArgs<GraphInitialParameters> => {
  const [linkArgs, setLinkArgs] = useState<
    undefined | GraphLinkArgs<GraphInitialParameters>
  >(undefined);
  const navigate = useNavigate();
  const location = useIpfLocation();
  const { state } = location;

  useEffect(() => {
    if (state?.graphLinkArgs) {
      setLinkArgs(state?.graphLinkArgs);
    }
  }, [state]);

  useManagedDomEventListeners([
    window,
    'beforeunload',
    () =>
      replaceHistoryStateField(navigate, location, {
        graphLinkArgs: undefined,
      }),
  ]);

  return linkArgs;
};

const BareCanvasGraphPage: FC = () => {
  const snapshotId = useSnapshotId();
  if (snapshotId === null) {
    throw new IpfError(
      'INVALID_STATE',
      'Graphs cannot be rendered without active snapshot',
    );
  }
  const linkArgs = useGraphLinkArgs();

  const initialParameters = useKeepIdentityIfEqual(
    eqDeepStrict,
    linkArgs?.parameters ?? null,
  );
  const initialSettings = useKeepIdentityIfEqual(
    eqDeepStrict,
    linkArgs?.settings ?? null,
  );

  const graphHook = useGraph(snapshotId, initialParameters, initialSettings);

  // biome-ignore lint/correctness/useExhaustiveDependencies(graphHook.dispatch): considered stable
  useEffect(() => {
    const pathLookupDataInputTabId = linkArgs?.pathLookupDataInputTabId;
    const contentId = linkArgs?.sidePanelContentId;

    if (
      pathLookupDataInputTabId &&
      isPathLookupDataInputTabId(pathLookupDataInputTabId)
    ) {
      graphHook.dispatch(showSidePanel, {
        contentId: contentId ?? 'pathlookup',
        pathLookupDataInputTabId,
      });
    } else if (contentId && isGraphSidePanelContentId(contentId)) {
      graphHook.dispatch(showSidePanel, { contentId });
    }
  }, [linkArgs]);

  return <Graph model={graphHook} />;
};

BareCanvasGraphPage.whyDidYouRender = true;

export const CanvasGraphPage = memo(BareCanvasGraphPage);
